import { defineStore } from 'pinia'

import { ADMIN_ROLES } from '@/constants/permissions'
import Auth from '@/services/models/Auth'
import DigitalAccounts from '@/services/models/DigitalAccounts.js'
import Features from '@/services/models/Features.js'

export const useAuthStore = defineStore('authStore', {
  id: 'auth',
  state: () => ({
    status: '',
    client_id: '',
    auth_data: {},
    tokenData: {},
    featureList: [],
    digitalAccountsList: [],
    selectedAccount: {},
    links: [],
  }),
  actions: {
    login(data) {
      this.status = 'success'
      this.tokenData = JSON.parse(atob(data.access.split('.')[1]))
      localStorage.setItem('access', data.access)
      localStorage.setItem('refresh', data.refresh)
    },
    async me(data) {
      this.auth_data = data
      this.featureList = data.link_features?.enabled_features || []
      if (data.link) {
        this.digitalAccounts()
      }
    },
    async refreshMe() {
      try {
        const resp = await Auth.me()
        await this.me(resp.data)
        return resp.data
      } catch (error) {
        throw error
      }
    },
    setLink(link) {
      return new Promise(resolve => {
        this.client_id = link
        resolve(true)
      })
    },
    removeLink() {
      return new Promise(resolve => {
        this.client_id = ''
        this.auth_data.link = {}
        resolve(true)
      })
    },
    logout() {
      localStorage.removeItem('access')
      localStorage.removeItem('refresh')
      this.$reset()
    },
    async features(isAdmin) {
      this.featureList = await Features.getFeaturesByClient(this.auth_data?.link?.id, isAdmin)
    },
    digitalAccounts() {
      DigitalAccounts.list('?custom_page_size=-1').then(resp => {
        this.digitalAccountsList = resp.data.results
        this.selectedAccount = this.auth_data?.link?.digital_account || null
      })
    },
    update_picture(picture) {
      this.auth_data.link.picture = picture
    },
  },
  getters: {
    isLoggedIn: state => state.status === 'success',
    isLinkedIn: state => state.client_id !== '',
    isCompany: state => state.auth_data.link.resourcetype === 'Company',
    authStatus: state => state.status,
    authData: state => state.auth_data,
    userId: state => state.auth_data.user.id,
    linkId: state => state.client_id,
    link: state => state.auth_data.link,
    isValidated: state => !!state.auth_data.link?.validated,
    isSuperuser: state => state.auth_data.user.is_superuser || false,
    isAdmin: state => state.auth_data.user.is_superuser || state.auth_data.user.is_staff || false,
    isAdminMaster: state => state.auth_data.user.admin_role === ADMIN_ROLES.ADMIN_MASTER || false,
    getUserFeatures: state => state.featureList,
    getDigitalAccountsList: state =>
      state.digitalAccountsList.filter(element => element.id != state.selectedAccount.id),
    getSelectedAccount: state => state.selectedAccount,
    getOwnContactId: state =>
      state.auth_data.user.is_superuser ? '' : state.auth_data.link.own_contact,
    getOwnContactInfo: state => state.auth_data.link_own_contact,
    // TODO fix pulling isStaff from back-end
    isStaff: () => false,
    adminPermissions: state => state.auth_data?.user?.admin_permissions || [],
    adminRole: state => state.auth_data?.user?.admin_role || [],
    hasPermission: state => permission => {
      if (state.auth_data?.user?.admin_role === ADMIN_ROLES.ADMIN_MASTER) {
        return true
      }

      const permissions = state.auth_data?.user?.admin_permissions || []

      if (Array.isArray(permission)) {
        return permission.some(p => permissions.includes(p))
      }

      return permissions.includes(permission)
    },
    hasAllPermissions: state => permissionList => {
      if (state.auth_data?.user?.admin_role === ADMIN_ROLES.ADMIN_MASTER) {
        return true
      }

      const permissions = state.auth_data?.user?.admin_permissions || []
      return permissionList.every(p => permissions.includes(p))
    },
    userPermissions: state => state.auth_data?.user?.user_permissions || [],
    hasUserPermission: state => permission => {
      const permissions = state.auth_data?.user?.user_permissions || []
      if (Array.isArray(permission)) {
        return permission.some(p => permissions.includes(p))
      }
      return permissions.includes(permission)
    },
  },
  persist: true,
})
